import React, { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "./AuthContext";
import { userApi } from "../api/user";

/**
 * Context for usage event tracking
 */
const UsageEventContext = createContext({});

/**
 * Hook to access the usage event context
 */
export const useUsageEventContext = () => {
  return useContext(UsageEventContext);
};

/**
 * Props for UsageEventProvider
 */
interface UsageEventProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that automatically tracks usage events for route changes
 *
 * This provider should wrap the entire app to automatically track when users
 * visit different pages. It supports both authenticated and anonymous users.
 */
export const UsageEventProvider: React.FC<UsageEventProviderProps> = ({
  children,
}) => {
  const { user, isLoading } = useAuthContext();
  const location = useLocation();

  /**
   * Track usage event for the current route
   */
  const trackCurrentRoute = async () => {
    // Don't track while auth is still loading
    if (isLoading) {
      return;
    }
    // Get the user ID and web route
    const userId = user?._id || null;
    const webRoute = location.pathname;

    // Track the usage event
    try {
      await userApi.trackUsageEvent(webRoute, userId);
    } catch (error) {
      // Log error but don't throw - usage tracking shouldn't break the app
      console.error("Failed to track usage event:", error);
    }
  };

  // Track usage event whenever the route changes or auth loading finishes
  useEffect(() => {
    trackCurrentRoute();

    return () => {};
  }, [location.pathname, isLoading]);

  return (
    <UsageEventContext.Provider value={{}}>
      {children}
    </UsageEventContext.Provider>
  );
};
